import React, { FunctionComponent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Download, Loader } from 'lucide-react';
import { SharedVideo } from '@companion-professional/dctypes';
import { VideoPlayer } from '@companion-professional/components/VideoPlayer';
import { PageFrame } from '../components/PageFrame';
import { useDownloadSharedVideo } from '../hooks/useDownloadSharedVideo';
import { useVideoShare } from '../hooks/useVideoShare';

type ShareParams = {
  shortCode: string;
};

// VideoSharePage resolves the share short code from the URL and plays the shared videos, one at a time, with buttons to
// step forward and back through them. Each video is shown with the details identifying it and a download button.
export const VideoSharePage: FunctionComponent = () => {
  const { shortCode } = useParams<ShareParams>();
  const { isPending, isError, isNotFound, createdAt, items } = useVideoShare(shortCode);

  // The index of the video currently shown. Clamped to the bounds of items by the navigation handlers.
  const [currentIndex, setCurrentIndex] = useState(0);

  if (isPending) {
    return (
      <PageFrame>
        <p className="text-on-background/70 text-lg">Loading shared videos…</p>
      </PageFrame>
    );
  }

  if (isNotFound) {
    return (
      <PageFrame>
        <p className="text-on-background/70 max-w-md text-center text-lg">
          This share link is no longer valid. It may have expired or been entered incorrectly.
        </p>
      </PageFrame>
    );
  }

  if (isError) {
    return (
      <PageFrame>
        <p className="text-on-background/70 max-w-md text-center text-lg">
          Something went wrong loading these videos. Please try again in a moment.
        </p>
      </PageFrame>
    );
  }

  if (items.length === 0) {
    return (
      <PageFrame exportedAt={createdAt}>
        <p className="text-on-background/70 text-lg">There are no videos in this share.</p>
      </PageFrame>
    );
  }

  // currentIndex is always in bounds here (the handlers clamp it and items is non-empty above), but index access is
  // typed as possibly-undefined; this guard narrows the type and can't actually be hit at runtime.
  const currentItem = items[currentIndex];
  // shortCode is always set here: the route only matches with a code, and useVideoShare stays disabled (pending)
  // without one. The check narrows the type.
  if (!currentItem || !shortCode) {
    return null;
  }

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;

  return (
    <PageFrame exportedAt={createdAt}>
      <div className="flex w-full max-w-3xl flex-col items-center gap-4">
        <VideoDetails item={currentItem} />

        <div className="w-full overflow-hidden rounded-lg bg-black shadow-md">
          <VideoPlayer
            // Remounting on each video (keyed by item id) guarantees the player loads the newly selected source.
            key={currentItem.item_id}
            muxSignedPlaybackUrl={currentItem.item_info.signed_playback_url}
            className="aspect-video w-full"
          />
        </div>

        <DownloadButton
          // Keyed by item so a failure message from one video doesn't linger after moving to another.
          key={currentItem.item_id}
          shortCode={shortCode}
          itemId={currentItem.item_id}
        />

        <div className="flex w-full items-center justify-between gap-4">
          <NavButton
            direction="previous"
            disabled={!hasPrevious}
            onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
          />
          <span className="text-on-background/70 text-md font-medium">
            Video {currentIndex + 1} of {items.length}
          </span>
          <NavButton
            direction="next"
            disabled={!hasNext}
            onClick={() => setCurrentIndex((i) => Math.min(items.length - 1, i + 1))}
          />
        </div>
      </div>
    </PageFrame>
  );
};

// formatUploadedAt renders an ISO timestamp as a date and time in the viewer's own locale and time zone.
const formatUploadedAt = (uploadedAt: string) =>
  new Date(uploadedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

// VideoDetails identifies a shared video: the pet, the clinic's id for it, the clinic it was submitted to, and when it
// was uploaded. The upload time is omitted when the server has none.
const VideoDetails: FunctionComponent<{ item: SharedVideo }> = ({ item }) => (
  <div className="w-full">
    <h2 className="text-2xl font-semibold text-on-background">{item.pet_name}</h2>
    <dl className="text-on-background/70 mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
      <dt className="font-medium">Pet ID</dt>
      <dd>{item.clinic_pet_id}</dd>
      <dt className="font-medium">Clinic</dt>
      <dd>{item.clinic_name}</dd>
      {item.uploaded_at && (
        <>
          <dt className="font-medium">Uploaded</dt>
          <dd>{formatUploadedAt(item.uploaded_at)}</dd>
        </>
      )}
    </dl>
  </div>
);

interface DownloadButtonProps {
  shortCode: string;
  itemId: string;
}

// DownloadButton downloads the original upload of one shared video, showing a spinner while the download URL is fetched
// and a message below the button if that fails.
const DownloadButton: FunctionComponent<DownloadButtonProps> = ({ shortCode, itemId }) => {
  const { isDownloading, errorMessage, downloadVideo } = useDownloadSharedVideo(shortCode);
  return (
    <div className="flex w-full flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => downloadVideo(itemId)}
        disabled={isDownloading}
        className="flex items-center gap-2 rounded-md border border-primary px-3 py-1.5 text-sm font-medium text-primary transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isDownloading ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
        Download
      </button>
      {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
    </div>
  );
};

interface NavButtonProps {
  direction: 'previous' | 'next';
  disabled: boolean;
  onClick: () => void;
}

// NavButton is a single forward/back control. It carries an accessible label since its only visible content is an icon.
const NavButton: FunctionComponent<NavButtonProps> = ({ direction, disabled, onClick }) => {
  const isNext = direction === 'next';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={isNext ? 'Next video' : 'Previous video'}
      className="flex items-center gap-1 rounded-md bg-primary px-4 py-2 font-medium text-on-primary transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
    >
      {isNext ? (
        <>
          Next
          <ChevronRight className="h-5 w-5" />
        </>
      ) : (
        <>
          <ChevronLeft className="h-5 w-5" />
          Previous
        </>
      )}
    </button>
  );
};
